import React, { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { API, axios } from '../../utils/api';
import { classi, messaggioErrore, useInvioUnico } from '../accesso/campi';
import {
  FASI, saluto, dataLunga, titoloFase, titoloVisita, quandoGiorno, alleOra, metriQuadri, titoloImmobile,
  daCompilare, leggiDataUtcRoma, linkWhatsApp, linkChiama, fraseDocumento, conArticoloDocumento,
  passiMutuo, STATI_PROPOSTA_CHIUSI, disponibilitaGiornoDiRoma, adessoRoma, appuntamentiDiOggi, mutuoRifiutato, conAnno,
  chiaveFrasiFase, nomePassoMutuo,
} from '../../utils/percorso';
import Icona from './Icone';
import { riassuntoGriglia, testoAggiornata } from '../../utils/agenda';
import { titoloMancano } from '../../utils/documenti';
import { testoPrezzo, controproposta, testoControproposta } from '../../utils/immobili';
import { useTraduzioni, PRINCIPALE } from '../../i18n';
import RigaVisitaCliente from './RigaVisitaCliente';

// Sezioni della home del cliente (bozza design/home-cliente-v8.html). La card "Oggi" e la card
// "Com'è andata?" stanno in file a parte (CardOggi.jsx, CardEsito.jsx).
// I testi vengono dal dizionario della lingua del cliente: const { t, tp, l } = useTraduzioni().

const MESI_BREVI = ['gen', 'feb', 'mar', 'apr', 'mag', 'giu', 'lug', 'ago', 'set', 'ott', 'nov', 'dic'];
// il mese breve della data, nella lingua del cliente ("SET", "СЕНТ.")
const meseBreve = (d, l, data) => (l === PRINCIPALE ? MESI_BREVI[d.getMonth()] : data(d, { month: 'short', timeZone: undefined }));

// Scossone della card su un errore, come nel login
export const useScossone = () => {
  const [scossa, setScossa] = useState(false);
  const scuoti = () => {
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;
    setScossa(false);
    requestAnimationFrame(() => setScossa(true));
  };
  const fine = (e) => { if (e.animationName === 'mkc-shake') setScossa(false); };
  return { scossa, scuoti, fine };
};

// Una card i cui dati non sono arrivati (errore del server): lo dice, con "Riprovate", invece di un falso "niente".
// "Le novità non sono disponibili…" (prima senza il verbo: "Le novità non disponibili in questo momento")
const NonDisponibile = ({ chiave, onRiprova }) => {
  const { t } = useTraduzioni();
  return (
    <p className="vuoto-card non-disponibile" role="status">
      {t(chiave)}{' '}
      <a href="#riprova" onClick={(e) => { e.preventDefault(); if (onRiprova) onRiprova(); }}><u>{t('comune.riprova')}</u></a>
    </p>
  );
};

export const FotoConsulente = ({ consulente, classe = 'ph' }) => (
  <div className={classe} aria-hidden="true">
    {consulente?.foto_url ? <img src={consulente.foto_url} alt="" /> : (consulente?.nome || 'M').charAt(0).toUpperCase()}
  </div>
);

// ---------------------------------------------------------------- intestazione

const CardConsulente = ({ consulente }) => {
  const { t } = useTraduzioni();
  const wa = linkWhatsApp(consulente.telefono);
  const tel = linkChiama(consulente.telefono);
  return (
    <div className="ccard" data-testid="card-consulente">
      <FotoConsulente consulente={consulente} />
      <div>
        <small>{t('home.consulente.titolo')}</small>
        <b>{consulente.nome}</b>
        <div className="bt">
          {wa && <a className="w" href={wa} target="_blank" rel="noopener noreferrer"><Icona nome="whatsapp" />WhatsApp</a>}
          {tel && <a href={tel}><Icona nome="telefono" />{t('home.consulente.chiama')}</a>}
        </div>
      </div>
    </div>
  );
};

// Il saluto con l'ora del telefono, come nel login (ScenaAccesso): con il telefono fuori dal fuso di Roma la home
// diceva "Buongiorno" (ora di Roma) e il login "Buonasera". Il giorno resta quello di Roma, come gli appuntamenti.
export const Intestazione = ({ cliente, adesso, fase, riepilogo, consulente }) => {
  const { t, l } = useTraduzioni();
  return (
    <div className="hero-top r">
      <div className="who">
        <div className="av" aria-hidden="true">{(cliente?.nome || '?').charAt(0).toUpperCase()}</div>
        <div>
          <div className="date" data-testid="saluto">{saluto(new Date(), l)} · {dataLunga(adesso, new Date(), l)}</div>
          <h1>{titoloFase(cliente?.nome, fase, l)}</h1>
          <div>
            <span className="fase" data-testid="fase">
              {t('home.fase', { numero: fase.numero, totale: FASI.length })} · {t(`home.fasi.${fase.chiave}`)}
            </span>
            <span className="passo">{t('home.prossimo_passo', { passo: t(`home.passi.${chiaveFrasiFase(fase, 'passo')}`) })}</span>
          </div>
          <p data-testid="riepilogo">{riepilogo}</p>
        </div>
      </div>
      <div className="side"><CardConsulente consulente={consulente} /></div>
    </div>
  );
};

// ---------------------------------------------------------------- la vostra ricerca

const Voce = ({ icona, valore, etichetta }) => {
  const { t } = useTraduzioni();
  return (
    <div className="it">
      <div className="i"><Icona nome={icona} /></div>
      <div><b className={valore ? undefined : 'vuoto'}>{valore || t('home.ricerca.da_definire')}</b><span>{etichetta}</span></div>
    </div>
  );
};

export const CardRicerca = ({ ricerca }) => {
  const { t } = useTraduzioni();
  return (
    <div className="search r" data-testid="ricerca">
      <div className="items">
        <Voce icona="euro" valore={ricerca.budget} etichetta={ricerca.budgetEtichetta || t('home.ricerca.budget_massimo')} />
        <Voce icona="luogo" valore={ricerca.zone} etichetta={t('home.ricerca.zone')} />
        <Voce icona="casa" valore={ricerca.cosa} etichetta={t('home.ricerca.cosa')} />
        {ricerca.avere && <Voce icona="stella" valore={ricerca.avere} etichetta={t('home.ricerca.avere')} />}
        {ricerca.conNoi && <Voce icona="calendario" valore={ricerca.conNoi.dal} etichetta={ricerca.conNoi.da} />}
      </div>
      <Link to="/profilo#ricerca">{t('home.ricerca.modifica')}</Link>
    </div>
  );
};

// ---------------------------------------------------------------- il vostro percorso

// gli stessi titoli del calendario del cliente e del .ics (testo_per_cliente in backend/agenda.py)
const TITOLI_APPUNTAMENTO = {
  proposta: (a, t) => t('calendario.titoli.proposta'),
  perizia: (a, t) => (a.proposta?.mutuo_banca ? t('calendario.titoli.perizia_banca', { banca: a.proposta.mutuo_banca }) : t('calendario.titoli.perizia')),
  delibera: (a, t) => (a.proposta?.mutuo_banca ? t('calendario.titoli.delibera_banca', { banca: a.proposta.mutuo_banca }) : t('calendario.titoli.delibera')),
  compromesso: (a, t) => t('calendario.titoli.compromesso'),
  rogito: (a, t) => t(a.previsto ? 'calendario.titoli.rogito_previsto' : 'calendario.titoli.rogito'),
  visita: (a, t, l) => titoloVisita(a.immobile?.titolo || a.visita?.immobile_titolo, l),
};

// "Dopo il mutuo: con la delibera, fissiamo il rogito" sta già sopra: qui una frase diversa (anche per la Ricerca:
// prima "Quando troviamo la casa giusta, vi proponiamo una visita" ripeteva quella di sopra).
// Fase Proposta senza nessuna offerta mandata (vi è piaciuta una casa, la proposta è da preparare): non c'è
// un'offerta a cui l'agenzia possa rispondere
const fraseSenzaAppuntamenti = (fase, proposte, consulente, t, l) => {
  if (fase.chiave === 'proposta' && !proposte.some((p) => !STATI_PROPOSTA_CHIUSI.includes(p.stato))) {
    return consulente?.nome ? t('home.percorso.prepariamo_con', { nome: consulente.nome }) : t('home.percorso.prepariamo');
  }
  // l'ultima voce dello storico è la controproposta del venditore: non si aspetta l'agenzia, tocca a voi
  const risposta = fase.chiave === 'proposta' ? proposte.map((p) => controproposta(p, l)).find(Boolean) : null;
  if (risposta) return testoControproposta(risposta, consulente?.nome, l);
  // mutuo rifiutato: non ci sono più "passi della banca" da aspettare (la frase del rifiuto sta sopra)
  if (fase.chiave === 'mutuo' && mutuoRifiutato(proposte)) return t('home.percorso.strada_giusta');
  return t(`home.percorso.senza.${fase.chiave}`);
};

export const testoMutuoRifiutato = (consulente, t) => t('home.mutuo.rifiutato', { nome: consulente?.nome || t('comune.il_consulente') });

// I passi della banca nella fase Mutuo (istruttoria → perizia → delibera → erogazione)
// adesso: per scrivere l'anno sulle date di un altro anno ("20 gen 2027"; prima solo "20 gen", conAnno)
const PassiMutuo = ({ mutuo, adesso }) => {
  const { t, l, data } = useTraduzioni();
  return (
    <div className="passi-mutuo" data-testid="passi-mutuo">
      <span className="k">{mutuo.banca ? t('home.mutuo.banca_nome', { banca: mutuo.banca }) : t('home.mutuo.banca')}</span>
      <ol>
        {mutuo.passi.map((p) => (
          <li key={p.chiave} className={p.stato}>
            <i aria-hidden="true" />
            <b>{nomePassoMutuo(p.chiave, l)}</b>
            <small>{p.data ? conAnno(`${p.data.getDate()} ${meseBreve(p.data, l, data)}`, p.data, adesso) : p.stato === 'ora' ? t('home.mutuo.in_corso') : ''}</small>
          </li>
        ))}
      </ol>
    </div>
  );
};

// Sotto «Nessun altro appuntamento fissato», con un appuntamento oggi (il più importante per primo)
const DOPO_OGGI = ['rogito', 'compromesso', 'delibera', 'perizia', 'proposta', 'visita'];

export const CardPercorso = ({ fase, prossimo, oggi, adesso, proposte = [], consulente = null }) => {
  const { t, l, data } = useTraduzioni();
  const mutuo = fase.chiave === 'mutuo' ? passiMutuo(proposte, adesso) : null;
  let dettaglio = null;
  if (prossimo) {
    const dove = prossimo.tipo === 'rogito' ? prossimo.proposta?.rogito_luogo : prossimo.immobile?.citta;
    const domani = quandoGiorno(prossimo.data, adesso, l) === t('calendario.domani');
    dettaglio = [
      // un rogito solo previsto (mutuo ancora in banca) non ha un'ora: si fissa dopo la delibera
      // dataLunga con adesso: un appuntamento di un altro anno porta l'anno ("mercoledì 20 gennaio 2027")
      `${domani ? `${t('calendario.domani')}, ` : ''}${dataLunga(prossimo.data, adesso, l)}${prossimo.previsto ? '' : alleOra(prossimo.data, l)}`,
      // la casa per proposta, perizia, delibera e compromesso (non hanno un indirizzo proprio); il luogo per il rogito
      // e la città per la visita
      ['proposta', 'perizia', 'delibera', 'compromesso'].includes(prossimo.tipo) ? prossimo.immobile?.titolo : dove,
    ].filter(Boolean).join(' · ');
  }
  // l'appuntamento di oggi (sta nella card Oggi): la frase sotto parla di quello. Prima solo la visita: il giorno del
  // rogito diceva «Vi avvisiamo appena il notaio conferma la data» con «Rogito dal notaio» nella card Oggi
  // (rogito e perizia valgono tutto il giorno, anche passata l'ora: la card Oggi allora non c'è più, ma la frase del
  // notaio che «conferma la data» resterebbe sbagliata)
  const inizioGiorno = new Date(adesso.getFullYear(), adesso.getMonth(), adesso.getDate());
  const tuttiDiOggi = [...oggi, ...appuntamentiDiOggi([], proposte, [], inizioGiorno).filter((a) => ['rogito', 'perizia', 'delibera'].includes(a.tipo))];
  // un rogito solo previsto non è «oggi il rogito dal notaio»: la data si conferma dopo la delibera
  const diOggi = DOPO_OGGI.find((tipo) => tuttiDiOggi.some((a) => a.tipo === tipo && !a.previsto));
  const banca = mutuoRifiutato(proposte)?.mutuo_banca;
  return (
    <section className="card hero r" data-testid="card-percorso">
      <div className="ttl">
        <div className="i"><Icona nome="casa" /></div><h2>{t('home.percorso.titolo')}</h2>
        <Link to="/calendario">{t('home.percorso.calendario')}</Link>
      </div>
      <div className="pipe" aria-hidden="true">
        {FASI.map((f) => <i key={f.chiave} className={f.numero < fase.numero ? 'd' : f.numero === fase.numero ? 'c' : undefined} />)}
      </div>
      <div className="pipe-l">
        {FASI.map((f) => (f.numero === fase.numero
          ? <b key={f.chiave}>{t(`home.fasi.${f.chiave}`)}</b> : <span key={f.chiave}>{t(`home.fasi.${f.chiave}`)}</span>))}
      </div>
      {mutuo && <PassiMutuo mutuo={mutuo} adesso={adesso} />}
      {fase.chiave === 'mutuo' && mutuoRifiutato(proposte) && (
        <div className="passi-mutuo" data-testid="mutuo-rifiutato">
          <span className="k">{banca ? t('home.mutuo.banca_nome', { banca }) : t('home.mutuo.banca')}</span>
          <p style={{ margin: 0, fontSize: '.88rem', color: 'var(--paper)' }}>{testoMutuoRifiutato(consulente, t)}</p>
        </div>
      )}
      <div className="after">
        <Icona nome="freccia" />
        <span><b>{t(`home.dopo_titolo.${fase.chiave}`)}</b> {t(`home.dopo_testo.${chiaveFrasiFase(fase, 'dopo')}`)}</span>
      </div>
      {prossimo ? (
        <div className="next" data-testid="prossimo">
          {/* con l'anno se l'appuntamento è di un altro anno (conAnno): prima "20 GEN" sembrava di quest'anno */}
          <div className="d">{prossimo.data.getDate()}<small>{conAnno(meseBreve(prossimo.data, l, data), prossimo.data, adesso)}</small></div>
          <div><b>{TITOLI_APPUNTAMENTO[prossimo.tipo](prossimo, t, l)}</b><span>{dettaglio}</span></div>
        </div>
      ) : (
        <div className="next" data-testid="prossimo">
          <div className="d nessuno">—</div>
          <div>
            <b>{t('home.percorso.nessun_appuntamento')}</b>
            <span>{diOggi ? t(`home.percorso.oggi.${diOggi}`) : fraseSenzaAppuntamenti(fase, proposte, consulente, t, l)}</span>
          </div>
        </div>
      )}
    </section>
  );
};

// ---------------------------------------------------------------- i vostri immobili

const fotoValida = (url) => typeof url === 'string' && (/^https?:\/\//.test(url) || url.startsWith('/api/files/'));

const FotoImmobile = ({ immobile }) => {
  const [rotta, setRotta] = useState(false);
  const url = fotoValida(immobile.immagine_url) && !rotta ? immobile.immagine_url : null;
  return (
    <div className={classi('im', !url && daCompilare(immobile) && 'vuota')}>
      {url && <img src={url} alt="" loading="lazy" onError={() => setRotta(true)} />}
    </div>
  );
};

// Il link conta come il titolo di /immobili ("7 case"): le case in gioco, senza le scartate
export const CardImmobili = ({ recenti, totale, inGioco = totale, adesso, consulente, onFatto, onRifiuto, visite = null }) => {
  const { t, tp, l } = useTraduzioni();
  return (
    <section className="card r" data-testid="card-immobili">
      <div className="ttl">
        <div className="i b"><Icona nome="immobili" /></div><h2>{t('home.immobili.titolo')}</h2>
        {totale > 0 && <Link to="/immobili">{inGioco > 0 ? tp('home.immobili.plurali.link_case', inGioco) : t('home.immobili.tutti')}</Link>}
      </div>
      {recenti.length === 0 ? (
        <p className="vuoto-card">{t(totale === 0 ? 'home.immobili.vuoto_mai' : 'home.immobili.vuoto_ora')}</p>
      ) : recenti.map(({ immobile, etichetta }, i) => {
        const compilare = daCompilare(immobile);
        const aggiunto = leggiDataUtcRoma(immobile.data_aggiunta); // il giorno di Roma, come adesso
        // parole della card di /immobili: "Prezzo da verificare", non "1 €"
        const prezzo = [testoPrezzo(immobile, l), metriQuadri(immobile.superficie)].filter(Boolean).join(' · ');
        return (
          <div className="prop" key={immobile.id}>
            <FotoImmobile immobile={immobile} />
            <div>
              <h4 className={compilare ? 'muto' : undefined}>{titoloImmobile(immobile, l)}</h4>
              <div className={classi('pr', compilare && 'muto')}>
                {compilare
                  ? t('home.immobili.aggiunto', { quando: aggiunto ? quandoGiorno(aggiunto, adesso, l) : t('home.immobili.da_poco') })
                  : prezzo || t('home.immobili.prezzo_da_definire')}
              </div>
              <span className={`tag t-${etichetta.classe}`}>{etichetta.testo}</span>
              <RigaVisitaCliente immobile={immobile} consulente={consulente} onFatto={onFatto} onRifiuto={onRifiuto} adesso={adesso} visite={visite} inHome />
            </div>
            <Link className={classi('btn sm', i === recenti.length - 1 && 'p')} to={`/immobili/${immobile.id}`}>{t('home.immobili.apri')}</Link>
          </div>
        );
      })}
    </section>
  );
};

// ---------------------------------------------------------------- avete visto una casa?

// Campo di testo, non "url": il browser rifiuterebbe "www.idealista.it/…" e il testo condiviso dalle app
// ("Guarda questa casa! https://…"); il link lo estrae il server. Mentre si aggiunge, un altro invio non parte.
export const CardAggiungi = ({ clienteId, onAggiunto }) => {
  const { t, errore: tradErrore } = useTraduzioni();
  const [link, setLink] = useState('');
  const [attesa, setAttesa] = useState(false);
  const [avviso, setAvviso] = useState(null);
  const inCorso = useRef(false);
  const { scossa, scuoti, fine } = useScossone();

  const aggiungi = async (e) => {
    e.preventDefault();
    if (inCorso.current) return;
    if (!link.trim()) {
      setAvviso({ testo: t('home.aggiungi.manca_link') });
      scuoti();
      return;
    }
    inCorso.current = true;
    setAttesa(true);
    setAvviso(null);
    try {
      await axios.post(`${API}/clienti/${clienteId}/immobili`, { link: link.trim() });
      setLink('');
      setAvviso({ ok: true, testo: t('home.aggiungi.fatto') });
      onAggiunto();
    } catch (error) {
      // il motivo del server arriva in italiano: se la sua traduzione c'è si mostra quella (errori del dizionario)
      setAvviso({ testo: tradErrore(messaggioErrore(error, t('home.aggiungi.errore'))) });
      scuoti();
    } finally {
      inCorso.current = false;
      setAttesa(false);
    }
  };

  return (
    <section className={classi('card r', scossa && 'shake')} onAnimationEnd={fine} data-testid="card-aggiungi">
      <div className="ttl"><div className="i"><Icona nome="piu" /></div><h2>{t('home.aggiungi.titolo')}</h2></div>
      <form className="add" onSubmit={aggiungi} noValidate>
        <input type="text" inputMode="url" autoComplete="off" autoCapitalize="none" spellCheck={false} value={link}
          onChange={(e) => setLink(e.target.value)} maxLength={2000}
          placeholder={t('home.aggiungi.segnaposto')} aria-label={t('home.aggiungi.etichetta')} data-testid="link-annuncio" />
        <button type="submit" className={classi('btn p', attesa && 'busy')} aria-busy={attesa} disabled={attesa}>{t('home.aggiungi.bottone')}</button>
      </form>
      {avviso && <div className={classi('err', avviso.ok && 'ok')} role={avviso.ok ? 'status' : 'alert'}>{avviso.testo}</div>}
      <p className="hint">{t('home.aggiungi.nota')}</p>
    </section>
  );
};

// ---------------------------------------------------------------- colonna destra

export const CardNovita = ({ voci, tutte, nonDisponibile = false, onRiprova }) => {
  const { t } = useTraduzioni();
  const [aperta, setAperta] = useState(false);
  const elenco = aperta ? tutte : voci;
  return (
    <section className="card r" data-testid="card-novita">
      <div className="ttl">
        <div className="i"><Icona nome="orologio" /></div><h2>{t('home.novita')}</h2>
        {tutte.length > voci.length && (
          <a href="#novita" role="button" aria-expanded={aperta} onClick={(e) => { e.preventDefault(); setAperta(!aperta); }}>
            {t(aperta ? 'comune.meno' : 'comune.tutto')}
          </a>
        )}
      </div>
      {nonDisponibile ? <NonDisponibile chiave="home.novita_non_disponibili" onRiprova={onRiprova} />
        : elenco.length === 0 ? <p className="vuoto-card">{t('home.novita_vuote')}</p> : (
        <div className="feed">
          {elenco.map((v) => (
            <div key={v.id}><i className={classi('dot', v.tipo)} /><div><b>{v.testo}</b><span>{v.quando}</span></div></div>
          ))}
        </div>
      )}
    </section>
  );
};

const MAX_DA_FARE = 3;

// La proposta che scade per prima: "… scade sabato 10 ottobre" (con la timeline nella scheda della casa). Col mese:
// fino a 30 giorni prima "scade sabato 10" non diceva di quale mese
// Dopo la controproposta del venditore (l'ultima voce dello storico) non si aspetta più lui: "Il venditore ha risposto
// con 375.000 €: ne parlate con Karolina." (prima "Il venditore deve rispondere entro quella data")
// adesso: per l'anno delle scadenze di un altro anno ("scade sabato 10 ottobre 2027", conAnno dentro dataLunga)
export const RigaProposta = ({ scadenza, consulente = null, adesso = new Date() }) => {
  const { t, l } = useTraduzioni();
  if (!scadenza) return null;
  const risposta = controproposta(scadenza.proposta, l);
  const titolo = scadenza.giorni === 0 ? t('home.proposta.scade_oggi', { dove: scadenza.dove })
    : scadenza.giorni === 1 ? t('home.proposta.scade_domani', { dove: scadenza.dove })
      : t('home.proposta.scade', { dove: scadenza.dove, data: dataLunga(scadenza.scade, adesso, l) });
  return (
    <div className="search r" data-testid="riga-proposta">
      <div className="items">
        <div className="it">
          <div className="i"><Icona nome="euro" /></div>
          <div>
            <b style={{ fontSize: '1.1rem' }}>{titolo}</b>
            <span>{risposta ? testoControproposta(risposta, consulente?.nome, l) : t('home.proposta.attesa')}</span>
          </div>
        </div>
      </div>
      {scadenza.immobileId && <Link to={`/immobili/${scadenza.immobileId}`}>{t('home.proposta.link')}</Link>}
    </div>
  );
};

// Riga discreta sotto "La vostra ricerca": la disponibilità per le visite manca, è vecchia (più di 14 giorni) oppure
// l'ha segnata lo staff ("controllate che siano giuste", con "Sono giuste" che la fa diventare vostra).
// I link portano alla scheda Disponibilità del profilo, in cima alla pagina (in /calendario la sezione è in fondo e
// sul telefono la pagina restava in alto). Se "Sono giuste" non va, lo dice sotto (prima nessun messaggio).
// adesso: l'ora di Roma della home. Il riassunto "per settimana" usava la settimana del telefono (a Tokyo, la
// domenica sera di Roma, già quella dopo)
export const RigaDisponibilita = ({ motivo, disponibilita, onConferma, adesso = adessoRoma() }) => {
  const { t, l } = useTraduzioni();
  const [attesa, setAttesa] = useState(false);
  const [errore, setErrore] = useState(null);
  const invio = useInvioUnico();
  if (!motivo) return null;
  const staff = motivo === 'dallo_staff';
  // vecchia (più di 14 giorni): prima solo la domanda, senza dire quale e senza "Sono ancora giuste"; ora il riassunto
  // e il bottone (la stessa conferma di quella segnata dallo staff: la stessa PUT)
  const vecchia = motivo === 'vecchia';
  const conRiassunto = staff || vecchia;
  const conferma = async () => {
    if (!invio.blocca()) return;
    setAttesa(true);
    setErrore(null);
    try {
      await onConferma();
    } catch (err) {
      setErrore(messaggioErrore(err, t('home.disponibilita.errore')));
    } finally {
      setAttesa(false);
      invio.sblocca();
    }
  };
  return (
    <div className="search r" data-testid="riga-disponibilita">
      <div className="items">
        <div className="it">
          <div className="i"><Icona nome="orologio" /></div>
          <div>
            <b style={{ fontSize: '1.1rem' }}>{t(staff ? 'home.disponibilita.staff' : motivo === 'vuota' ? 'home.disponibilita.vuota' : 'home.disponibilita.vecchia')}</b>
            <span data-testid="riassunto-disponibilita">{conRiassunto
              ? [riassuntoGriglia(disponibilita, adesso, l) || t('home.disponibilita.nessun_giorno'),
                testoAggiornata(disponibilitaGiornoDiRoma(disponibilita), false, l)].filter(Boolean).join(' · ')
              : t('home.disponibilita.spiegazione')}</span>
          </div>
        </div>
      </div>
      {conRiassunto && onConferma && (
        <button type="button" className={classi('btn sm', attesa && 'busy')} aria-busy={attesa} onClick={conferma} disabled={attesa}>
          {t(vecchia ? 'home.disponibilita.ancora_giuste' : 'home.disponibilita.sono_giuste')}
        </button>
      )}
      <Link to="/profilo#disponibilita">
        {t(staff ? 'home.disponibilita.controllatele' : motivo === 'vuota' ? 'home.disponibilita.indicatela' : 'home.disponibilita.controllatela')}
      </Link>
      {errore && <div className="err riga-errore" role="alert">{errore}</div>}
    </div>
  );
};

// Riga discreta: il profilo di lavoro e famiglia non c'è ancora (serve per la lista giusta dei documenti)
export const RigaProfilo = ({ mostra }) => {
  const { t } = useTraduzioni();
  if (!mostra) return null;
  return (
    <div className="search r" data-testid="riga-profilo">
      <div className="items">
        <div className="it">
          <div className="i"><Icona nome="profilo" /></div>
          <div>
            <b style={{ fontSize: '1.1rem' }}>{t('home.profilo.titolo')}</b>
            <span>{t('home.profilo.testo')}</span>
          </div>
        </div>
      </div>
      <Link to="/profilo#lavoro">{t('home.profilo.link')}</Link>
    </div>
  );
};

export const CardDaFare = ({ daFare, nonDisponibile = false, onRiprova }) => {
  const { t, tp } = useTraduzioni();
  return (
    <section className="card r" data-testid="card-da-fare">
      <div className="ttl"><div className="i w"><Icona nome="fatto" /></div><h2>{t('home.da_fare.titolo')}</h2></div>
      {nonDisponibile ? <NonDisponibile chiave="home.da_fare.non_disponibili" onRiprova={onRiprova} />
        : daFare.length === 0 ? <p className="vuoto-card">{t('home.da_fare.vuoto')}</p> : (
        <div className="rows">
          {daFare.slice(0, MAX_DA_FARE).map((d, i) => (
            <div key={`${d.link || ''}${d.nome}-${d.persona}`} data-testid="da-fare">
              <span className="num">{i + 1}</span>
              <div><b>{d.titolo}</b><span>{d.perche}</span>
                {d.entro && <span className={`entro${d.entro.giorni <= 3 ? ' presto' : ''}`}>{d.entro.testo}</span>}</div>
              <Link className={`btn sm${d.link ? ' p' : ''}`} to={d.link || '/documenti'}>{d.bottone || t('home.da_fare.carica')}</Link>
            </div>
          ))}
          {daFare.length > MAX_DA_FARE && (
            <div><Link className="altri" to="/documenti">{tp('home.da_fare.plurali.altri', daFare.length - MAX_DA_FARE)}</Link></div>
          )}
        </div>
      )}
    </section>
  );
};

const CIRCONFERENZA = 176; // 2π × 28

// Stessi conti e stessa frase della pagina Documenti ("Ne mancano 3, il resto c'è"): ricevuti su ricevuti + mancanti
export const CardDocumenti = ({ documenti, nonDisponibile = false, onRiprova }) => {
  const { t, tp, l } = useTraduzioni();
  const { ricevuti, mancano, totale, prossimo } = documenti;
  const primo = !prossimo ? '' : prossimo.firma
    ? `${t('home.documenti.prossimo_firma', { documento: conArticoloDocumento(prossimo.nome, l) })} `
    : `${t('home.documenti.prossimo', { documento: fraseDocumento(prossimo.nome, l) })} `;
  const quota = totale ? ricevuti / totale : 0;
  // in italiano il titolo ha la parola in risalto ("Ne mancano <em>3</em>, il resto c'è"); nelle altre lingue
  // il numero sta dentro la frase e il risalto non si può spezzare allo stesso modo
  const titolo = titoloMancano(mancano, ricevuti);
  const titoloTradotto = !mancano ? t(ricevuti ? 'home.documenti.tutti' : 'home.documenti.niente')
    : tp(ricevuti ? 'home.documenti.plurali.mancano' : 'home.documenti.plurali.servono', mancano);
  return (
    <section className="card r" data-testid="card-documenti">
      <div className="ttl"><div className="i"><Icona nome="documento" /></div><h2>{t('home.documenti.titolo')}</h2><Link to="/documenti">{t('home.documenti.apri')}</Link></div>
      {nonDisponibile ? <NonDisponibile chiave="home.documenti.non_disponibili" onRiprova={onRiprova} /> : totale ? (
        <div className="docs">
          <div className="anello" role="img" aria-label={t('home.documenti.anello', { ricevuti, totale })}>
            <svg viewBox="0 0 64 64" width="64" height="64" aria-hidden="true">
              <circle className="bg" cx="32" cy="32" r="28" />
              <circle className="fg" cx="32" cy="32" r="28" style={{ strokeDashoffset: CIRCONFERENZA * (1 - quota) }} />
            </svg>
            <b>{ricevuti}/{totale}</b>
          </div>
          <div>
            <b>{l === PRINCIPALE ? `${titolo.prima}${titolo.em}${titolo.dopo}` : titoloTradotto}</b>
            <p className="hint">
              {primo}{t(mancano ? 'home.documenti.resto' : 'home.documenti.se_serve')}
            </p>
          </div>
        </div>
      ) : <p className="vuoto-card">{t('home.documenti.vuoto')}</p>}
    </section>
  );
};
